import * as R from 'ramda';
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
// constants
import * as C from '../../../../constants';
// utilities
import * as U from '../../../../utilities';
// components
import {
  getAvailableForReports,
  reorderFieldsOnDrugEnd,
  mapFieldsWithSequenceFromIndex,
} from '../../../report-format-panel/helpers';
//////////////////////////////////////////////////

export const setEditReportFormProps = props => {
  const { usedReport, availableFields } = props;

  const {
    name,
    fields,
    orderFields,
    description,
    defaultReport,
  } = R.or(usedReport, {});

  const reportFields = R.sort(
    (elem1, elem2) => R.subtract(elem1.sequence, elem2.sequence),
    R.filter(
      ({ name }) => U.notEquals(name, C.FIELD_UUID),
      R.or(fields, []),
    ),
  );

  const sortFields = R.sort(
    (elem1, elem2) => R.subtract(elem1.sequence, elem2.sequence),
    R.filter(({ name }) => (
      U.notEquals(name, C.FIELD_UUID)
    ), R.or(orderFields, [])),
  );

  const filterFields = R.filter(
    ({ name }) => U.notEquals(name, C.FIELD_UUID),
    U.getOrElse(usedReport, 'searchCriteria', []),
  );

  const initialValues = { name, description, defaultReport };

  return R.mergeRight({
    sortFields,
    filterFields,
    reportFields,
    initialValues,
    availableFields,
  }, props);
};

const withReportFieldList = WrappedComponent => props => { // eslint-disable-line
  const updatedProps = useMemo(() => setEditReportFormProps(props), [props]);

  const { reportFields, availableFields } = updatedProps;

  const { reportFieldList, setReportFieldList } = props;

  const [defaultFieldsCount, setDefaultFieldsCount] = useState(5);
  const [availableForReports, setAvailableForReports] = useState(R.or(getAvailableForReports(props, reportFields), []));

  useEffect(() => {
    if (U.isNilOrEmpty(reportFields)) return;

    if (U.deepEquals(reportFields, reportFieldList)) return;

    setReportFieldList(reportFields);

    setAvailableForReports(getAvailableForReports(props, reportFields));
  }, []); // eslint-disable-line

  const handleAddReportField = useCallback(() => {
    const newField = {
      name: null,
      sequence: R.inc(R.length(reportFieldList)),
    };

    const fieldsUpdated = R.append(newField, reportFieldList);

    setReportFieldList(fieldsUpdated);
    setAvailableForReports(getAvailableForReports(props, fieldsUpdated));
  }, [props, reportFieldList, setReportFieldList]);

  const handleAddAllReportField = useCallback(() => {
    const filtered = R.filter(({ name }) => U.isNotNilAndNotEmpty(name), R.or(reportFieldList, []));
    const keys = R.compose(
      R.keys,
      R.indexBy(R.prop(C.FIELD_NAME)),
    )(filtered);
    const length = R.length(keys);
    const availableList = R.compose(
      U.mapIndexed(({ value }, index) => ({
        uuid: null,
        name: value,
        sequence: R.add(R.inc(index), length),
      })),
      R.reject(({ value }) => R.includes(value, keys)),
    )(availableFields);

    const result = R.concat(filtered, availableList);

    setReportFieldList(result);

    setAvailableForReports(getAvailableForReports(props, result));
  }, [availableFields, props, reportFieldList, setReportFieldList]);

  const handleAddReportFields = useCallback(() => {
    const newField = {
      name: null,
    };

    const newFields = R.times(() => R.clone(newField), defaultFieldsCount);
    const fieldsUpdated = R.concat(reportFieldList, newFields);

    setReportFieldList(fieldsUpdated);

    setAvailableForReports(getAvailableForReports(props, fieldsUpdated));
  }, [props, defaultFieldsCount, reportFieldList, setReportFieldList]);

  const handleRemoveReportField = useCallback(filterIndex => {
    const fieldsUpdated = mapFieldsWithSequenceFromIndex(R.dissocPath([filterIndex], reportFieldList));

    setReportFieldList(fieldsUpdated);

    setAvailableForReports(getAvailableForReports(props, fieldsUpdated));
  }, [props, reportFieldList, setReportFieldList]);

  const handleSelectReportFieldName = useCallback((values, fieldIndex) => {
    const name = R.pathOr('', [C.FIELD_NAME], values);
    const value = R.pathOr('', ['option', C.FIELD_VALUE], values);

    const newField = {
      sequence: fieldIndex,
      name: R.or(value, name),
    };

    const fieldsUpdated = R.assocPath([fieldIndex], newField, reportFieldList);

    setReportFieldList(fieldsUpdated);

    setAvailableForReports(getAvailableForReports(props, fieldsUpdated));
  }, [props, reportFieldList, setReportFieldList]);

  return (
    <WrappedComponent
      {...props}
      reportFieldList={reportFieldList}
      defaultFieldsCount={defaultFieldsCount}
      setReportFieldList={setReportFieldList}
      availableForReports={availableForReports}
      handleAddReportField={handleAddReportField}
      setDefaultFieldsCount={setDefaultFieldsCount}
      handleAddReportFields={handleAddReportFields}
      setAvailableForReports={setAvailableForReports}
      handleRemoveReportField={handleRemoveReportField}
      handleAddAllReportField={handleAddAllReportField}
      handleSelectReportFieldName={handleSelectReportFieldName}
    />
  );
};

const withDnDStartEnd = WrappedComponent => props => { // eslint-disable-line
  const [reportSortList, setReportSortList] = useState([]);
  const [dndProcessing, setDnDProcessing] = useState(false);
  const [reportFieldList, setReportFieldList] = useState([]);

  const handleDragStart = useCallback(() => {
    setDnDProcessing(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDnDProcessing(false);
  }, []);

  const handleReportFieldSortDragEnd = useCallback(result => {
    const reorderedFields = reorderFieldsOnDrugEnd(reportFieldList, result);

    setReportFieldList(reorderedFields);
  }, [reportFieldList, setReportFieldList]);

  const handleSortDragEnd = useCallback(result => {
    const reorderedFields = reorderFieldsOnDrugEnd(reportSortList, result);

    setReportSortList(reorderedFields);
  }, [reportSortList, setReportSortList]);

  return (
    <WrappedComponent
      {...props}
      dndProcessing={dndProcessing}
      handleDragEnd={handleDragEnd}
      reportSortList={reportSortList}
      reportFieldList={reportFieldList}
      handleDragStart={handleDragStart}
      setDnDProcessing={setDnDProcessing}
      setReportSortList={setReportSortList}
      handleSortDragEnd={handleSortDragEnd}
      setReportFieldList={setReportFieldList}
      handleReportFieldSortDragEnd={handleReportFieldSortDragEnd}
    />
  );
};

const withScrollToViewOnOpen = WrappedComponent => props => { // eslint-disable-line
  const { uuid } = props;

  const handleOpen = useCallback(() => {
    const el = document.getElementById(uuid);
    el.scrollIntoView();
  }, [uuid]);

  return <WrappedComponent {...props} handleOpen={handleOpen} />;
};

export {
  withDnDStartEnd,
  withReportFieldList,
  withScrollToViewOnOpen,
};
